import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { SIDEBAR_TEST_IDS } from '@/constants/testIds'
import { lockBodyScroll, unlockBodyScroll } from '@/lib/bodyScrollLock'

import './SidebarMobile.scss'

interface SidebarMobileProps {
  isOpen: boolean
  onClose: () => void
  /** The portal's sidebar, given `isOpen` and no expand/collapse toggle. */
  children: ReactNode
}

/**
 * The sidebar as an off-canvas drawer, for viewports at or below
 * `$breakpoint-mobile`.
 *
 * It renders the portal's own sidebar (`Sidebar` or `B2bSidebar`) — no separate
 * mobile navigation exists, and none should: the drawer has to carry the workspace
 * switcher, the nav, the profile menu and logout, which is the whole sidebar. What differs is the presentation
 * (see the drawer block at the bottom of `Sidebar/index.scss`, where `--open` /
 * `--collapsed` are re-skinned as the drawer's two positions) plus the scrim and
 * the dismiss behaviours owned here.
 *
 * `isOpen` is held by the caller rather than here because the app bar's trigger
 * needs it too, for its own `aria-expanded`.
 */
export function SidebarMobile({ isOpen, onClose, children }: SidebarMobileProps) {
  const { pathname } = useLocation()

  // Tapping a nav item navigates but would leave the drawer covering the page it
  // just opened. Closing on any path change covers every exit uniformly — nav
  // links, browser back, and programmatic redirects alike.
  useEffect(() => {
    onClose()
    // Intentionally keyed on the path alone: re-running when `onClose`'s identity
    // changes would close the drawer on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Escape closes it, matching the scrim tap.
  useEffect(() => {
    if (!isOpen) return undefined

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [isOpen, onClose])

  // Freeze the page behind an open drawer. Without this, a swipe that starts on
  // the scrim scrolls the content underneath, which reads as the drawer being
  // broken. Through the shared ref-counted lock rather than `body.style.overflow`
  // directly: Cookie Settings opens `widgets/CookieBanner` from inside this drawer,
  // and two writers to the same style would leave the page scrollable under the
  // still-open drawer once the banner closed.
  useEffect(() => {
    if (!isOpen) return undefined

    lockBodyScroll()
    return unlockBodyScroll
  }, [isOpen])

  return (
    <>
      {children}
      {isOpen && (
        <div
          className="sidebar-mobile__scrim"
          data-testid={SIDEBAR_TEST_IDS.MOBILE_DRAWER_SCRIM}
          // Purely a dismiss affordance: Escape covers the keyboard path and the
          // app bar's trigger is the accessible control, so this stays out of the
          // tab order rather than becoming a second focusable "close".
          aria-hidden="true"
          onClick={onClose}
        />
      )}
    </>
  )
}
